import { useEffect, useState } from "react";

interface Entry {
  id: string;
  caller: string;
  when: string | null;
  durationSec: number | null;
}

export function Voicemail(props: { token: string }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const res = await fetch("/api/voicemail", {
          headers: { authorization: `Bearer ${props.token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        const data = (await res.json()) as { entries: Entry[] };
        if (!cancelled) {
          setEntries(data.entries);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    }
    void load();
    // Poll every 15 s so a voicemail recorded while the agent is on
    // this tab shows up without a manual ↻ Refresh click.
    const t = setInterval(() => void load(), 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [props.token, refreshKey]);

  async function play(id: string): Promise<void> {
    if (audioUrls[id]) return;
    try {
      const res = await fetch(`/api/voicemail/${id}/audio`, {
        headers: { authorization: `Bearer ${props.token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      setAudioUrls((m) => ({ ...m, [id]: url }));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="card">
      <div className="card-header">
        <h3>Messages vocaux</h3>
        <div className="actions">
          <button className="ghost" onClick={() => setRefreshKey((k) => k + 1)}>
            ↻ Actualiser
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {entries == null && !error && <div className="empty">Chargement…</div>}
      {entries != null && entries.length === 0 && <div className="empty">Aucun message vocal.</div>}
      {entries != null && entries.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Reçu le</th>
              <th>Appelant</th>
              <th>Durée</th>
              <th>Audio</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>{e.when ? new Date(e.when).toLocaleString("fr-FR") : "—"}</td>
                <td><code>{e.caller}</code></td>
                <td>{e.durationSec != null ? `${e.durationSec} s` : "—"}</td>
                <td>
                  {audioUrls[e.id] ? (
                    <audio src={audioUrls[e.id]} controls style={{ height: "1.6rem", verticalAlign: "middle" }} />
                  ) : (
                    <button className="ghost" onClick={() => play(e.id)} type="button">
                      ▶ Écouter
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
